import {TFunction} from 'i18next';
import {convertVariablesToHtmlInline} from '../../textEditor/Variables';
import {TemplateStructure} from '../types/templateStructure';

/**
 * Apply formatting (bold/italic) to currently selected text
 * Uses native browser formatting commands to wrap text in <strong> or <em> tags
 * 
 * @param command - The format command ('bold' or 'italic')
 * @returns boolean - True if formatting was applied, false otherwise
 */
export function applyFormatToSelection(command: 'bold' | 'italic'): boolean {
    const selection = window.getSelection();
    if(!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return false;
    }    
    const range = selection.getRangeAt(0);
    
    // Check if selection contains a variable span (which should not be formatted)
    const container = range.commonAncestorContainer;
    const parentElement = container.nodeType === Node.TEXT_NODE 
        ? container.parentElement 
        : container as HTMLElement;
    
    if(parentElement?.closest('.variable')) {
        return false;
    }
    
    // Get the selected text
    const selectedText = selection.toString();
    if(!selectedText.trim()) {
        return false;
    }
    
    // Check if already formatted - if parent is the format tag, remove it (toggle off)
    const formatTag = command === 'bold' ? 'STRONG' : 'EM';
    const parentTag = parentElement?.tagName;
    
    if(parentTag === formatTag) {
        // Unwrap the format tag
        const parent = parentElement as HTMLElement;
        const fragment = document.createDocumentFragment();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = parent.innerHTML || '';
        while(tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
        }
        parent.parentNode?.replaceChild(fragment, parent);
        
        // Restore selection
        const newRange = document.createRange();
        newRange.selectNodeContents(fragment);
        selection.removeAllRanges();
        selection.addRange(newRange);
        return true;
    }
    
    // Apply formatting by wrapping in appropriate tag
    const formattedElement = document.createElement(command === 'bold' ? 'strong' : 'em');
    
    try {
        // Extract contents and wrap in format tag
        const contents = range.extractContents();
        formattedElement.appendChild(contents);
        range.insertNode(formattedElement);
        
        // Select the newly formatted text
        const newRange = document.createRange();
        newRange.selectNodeContents(formattedElement);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Convert HTML formatting tags to WhatsApp markdown syntax
 * <strong>text</strong> → *text*
 * <em>text</em> → _text_
 * 
 * @param html - HTML string with formatting tags
 * @returns String with WhatsApp markdown formatting
 */
export function convertHtmlToWhatsAppFormat(html: string): string {
    if(!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Process all strong tags (convert to *text*)
    const strongTags = Array.from(tempDiv.querySelectorAll('strong'));
    strongTags.reverse().forEach(strong => {
        const fragment = document.createDocumentFragment();
        fragment.appendChild(document.createTextNode('*'));
        while(strong.firstChild) {
            fragment.appendChild(strong.firstChild);
        }        
        fragment.appendChild(document.createTextNode('*'));
        strong.parentNode?.replaceChild(fragment, strong);
    });
    
    // Process all em tags (convert to _text_)
    const emTags = Array.from(tempDiv.querySelectorAll('em'));
    emTags.reverse().forEach(em => {
        const fragment = document.createDocumentFragment();
        fragment.appendChild(document.createTextNode('_'));
        while(em.firstChild) {
            fragment.appendChild(em.firstChild);
        }
        fragment.appendChild(document.createTextNode('_'));
        em.parentNode?.replaceChild(fragment, em);
    });
    
    return tempDiv.innerHTML;
}

/**
 * Convert WhatsApp markdown syntax to HTML formatting tags
 * *text* → <strong>text</strong>
 * _text_ → <em>text</em>
 * 
 * @param text - Text with WhatsApp markdown formatting
 * @returns HTML string with formatting tags
 */
export function convertWhatsAppFormatToHtml(text: string): string {
    if(!text) return '';
    let result = text;
    // Convert bold: *text* → <strong>text</strong>
    result = result.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<strong>$1</strong>');
    // Convert italic: _text_ → <em>text</em>
    result = result.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');
    
    return result;
}

export const convertToHtml = (value: string, defaultVariables: Record<string, string>, t: TFunction) => {
    const htmlContent = convertVariablesToHtmlInline(value, defaultVariables, t);
    return convertWhatsAppFormatToHtml(htmlContent);
};

/**
 * Parse old-service whatsappContent (plain string with \n and [var] placeholders) into TemplateStructure.
 * @param content - Plain message string
 * @returns TemplateStructure (text items per line, empty items for blank lines)
 */
export function whatsappContentToTemplateStructure(content: string): TemplateStructure {
    if(content == null || typeof content !== 'string') {
        return [];
    }
    const lines = content.split('\n');
    const result: TemplateStructure = [];
    let textIndex = 1;
    for(let i = 0; i < lines.length; i++) {
        const segment = lines[i];
        if(segment === '') {
            result.push({type: 'empty'});
        } else {
            result.push({type: 'text', value: segment, index: textIndex++});
        }
    }
    return result;
}

/**
 * Serialize TemplateStructure into plain text with inline params replaced
 * Filters out button items and joins all text content with newlines
 * 
 * @param structure - The TemplateStructure array
 * @returns Serialized text content
 */
export function serializeTemplateStructure(structure: any): string {
    if(!structure || !Array.isArray(structure)) return '';
    
    return structure
        .filter(item => item.type !== 'button')
        .map(item => {
            if(item.type === 'inline') {
                let text = item?.value || '';
                if(item.params) {
                    item.params.forEach(param => {
                        if(param.value) {
                            text = text.replace(`{{${param.index}}}`, param.value);
                        }
                    });
                }
                return text;
            }
            if(item.type === 'text') {
                return item.value;
            }
            if(item.type === 'empty') {
                return '';
            }
            return '';
        })
        .join('\n');
}
